/* eslint-disable react/jsx-props-no-spreading */
/**
 *
 * MonthPicker
 *
 */

import React, { useState } from 'react';
import _classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import InputBase from '@material-ui/core/InputBase';
import TextField from '@material-ui/core/TextField';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useDateMonthPickerLocale from 'components/useDateMonthPickerLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import Picker from './Picker';
import { isValidDateGenerator } from './utils';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
  },
  assessment: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    margin: '8px 0',
    padding: 8,
    border: '1px solid #D8D8D8',
    width: '100%',
    fontSize: 20,
    [theme.breakpoints.down('600')]: {
      width: '100%',
    },
  },
  root: {
    minWidth: 130,
    '& input': {
      height: '1.2em',
    },
    '& select': {
      height: '1.2em',
      lineHeight: '1.35',
    },
  },
  padded: {
    '& input': {
      padding: '16px 14px',
    },
    '& select': {
      padding: '16px 14px',
    },
  },
  fontSize: {
    fontSize: '1.063rem',
  },
  placeholderColor: {
    color: '#999999',
  },
  shrinkColor: {
    color: '#4B4B4B',
  },
}));

function MonthPicker({
  assessment,
  value,
  placeholder,
  plain,
  modal,
  onChange,
  preventDisabledDates = false,
  disabled,
  disableFutureDates,
  disablePreviousDates,
}) {
  const classes = useStyles();
  const [typedInput, setTypedInput] = useState(false);
  const [visible, setVisible] = useState(false);
  const locale = useDateMonthPickerLocale();
  const clientDetails = useSelector(makeSelectClientDetails());
  const [siteCopy] = useSiteCopySelector(['sign-up']);
  const [siteConfig] = useSiteConfigSelector(['Age and Gender Mappings']);
  const minimumAge = _get(
    siteConfig,
    ['config', 'monthYearConfig', 'clientConfig', clientDetails.shortName],
    _get(siteConfig, 'config.monthYearConfig.default', 13),
  );
  const isValidDate = isValidDateGenerator(
    disableFutureDates,
    disablePreviousDates,
    minimumAge,
  );

  const handleMouseDown = () => {
    setVisible(true);
  };

  const handleFocus = () => {
    setVisible(true);
  };

  const handleBlur = () => {
    setVisible(false);
    setTypedInput(false);

    if (value && value.match(/[0-9]{4}-[0-9]{2}/)) {
      if (!isValidDate(moment(value, 'yyyy-MM'))) {
        onChange(null);
      }
    } else {
      onChange(null);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (value.match(/[0-9]{4}-[0-9]{2}/)) {
        onChange(value);
        if (e.key === 'Enter') e.target.blur();
      } else if (value.match(/[0-9]{4}\/[0-9]{2}/)) {
        onChange(moment(value, 'yyyy/MM').format('yyyy-MM'));
        if (e.key === 'Enter') e.target.blur();
      } else if (value.match(/[0-9]{2}-[0-9]{4}/)) {
        onChange(moment(value, 'MM-yyyy').format('yyyy-MM'));
        if (e.key === 'Enter') e.target.blur();
      } else if (value.match(/[0-9]{2}\/[0-9]{4}/)) {
        onChange(moment(value, 'MM/yyyy').format('yyyy-MM'));
        if (e.key === 'Enter') e.target.blur();
      } else if (value.match(/[a-zA-Z], [0-9]{4}/)) {
        onChange(moment(value, 'MMMM, yyyy').format('yyyy-MM'));
        if (e.key === 'Enter') e.target.blur();
      } else if (value.match(/[a-zA-Z] [0-9]{4}/)) {
        onChange(moment(value, 'MMMM yyyy').format('yyyy-MM'));
        if (e.key === 'Enter') e.target.blur();
      } else {
        onChange(null);
      }
    } else if (
      'abcdefghijklmnopqrstuvxyzw0123456789, -/MetaAltGraphCapsLockFnFnLockNumLockScrollLockControlShift'.includes(
        e.key.toLowerCase(),
      )
    ) {
      setTypedInput(true);
    } else {
      onChange(null);
    }
  };

  const handleInputChange = e => {
    onChange(e.target.value);
    if (e.target.value.match(/[0-9]{4}-[0-9]{2}/)) {
      if (isValidDate(moment(e.target.value, 'yyyy-MM'))) {
        onChange(moment(e.target.value, 'yyyy-MM').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    } else if (e.target.value.match(/[0-9]{4}\/[0-9]{2}/)) {
      if (isValidDate(moment(e.target.value, 'yyyy/MM'))) {
        onChange(moment(e.target.value, 'yyyy/MM').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    } else if (e.target.value.match(/[0-9]{2}-[0-9]{4}/)) {
      if (isValidDate(moment(e.target.value, 'MM-yyyy'))) {
        onChange(moment(e.target.value, 'MM-yyyy').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    } else if (e.target.value.match(/[0-9]{2}\/[0-9]{4}/)) {
      if (isValidDate(moment(e.target.value, 'MM/yyyy'))) {
        onChange(moment(e.target.value, 'MM/yyyy').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    } else if (e.target.value.match(/[a-zA-Z]+, [0-9]{4}/)) {
      if (isValidDate(moment(e.target.value, 'MMMM, yyyy'))) {
        onChange(moment(e.target.value, 'MMMM, yyyy').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    } else if (e.target.value.match(/[a-zA-Z]+ [0-9]{4}/)) {
      if (isValidDate(moment(e.target.value, 'MMMM yyyy'))) {
        onChange(moment(e.target.value, 'MMMM yyyy').format('yyyy-MM'));
        setTypedInput(false);
      } else {
        onChange(null);
      }
    }
  };

  const handleChange = date => {
    if (typeof date === 'object') {
      if (isValidDate(date)) onChange(date.format('yyyy-MM'));
      else onChange(null);
    }
  };

  const handleSubmit = date => {
    onChange(date.format('yyyy-MM'));
    setVisible(false);
  };

  const getInput = () => {
    let finalValue = null;
    if (value) {
      if (typedInput) {
        finalValue = value;
      } else {
        finalValue = moment(value, 'yyyy-MM').format('MMMM, yyyy');
      }
    }

    const finalProps = {
      id: 'monthYear',
      name: 'monthYear',
      label: !assessment && _get(siteCopy, ['pageCopy', `agePlaceholder`]),
      onChange: handleInputChange,
      fullWidth: true,
      disabled,
      inputProps: {
        onMouseDown: handleMouseDown,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
      },
      placeholder: !assessment
        ? _get(siteCopy, 'pageCopy.monthYearLabel')
        : placeholder,
      variant: plain ? 'standard' : 'outlined',
      value: finalValue || '',
    };

    if (assessment)
      return (
        <InputBase classes={{ root: classes.assessment }} {...finalProps} />
      );
    return (
      <TextField
        classes={{
          root: _classNames(classes.root, { [classes.padded]: !plain }),
        }}
        InputProps={{
          classes: {
            root: classes.fontSize,
          },
          autoComplete: 'off',
          role: 'presentation',
        }}
        InputLabelProps={{
          classes: {
            root: _classNames(classes.fontSize, classes.placeholderColor),
            shrink: classes.shrinkColor,
          },
        }}
        {...finalProps}
      />
    );
  };

  return (
    <Box className={classes.container}>
      {getInput()}
      {visible && (
        <Picker
          value={
            value && value.match(/[0-9]{4}-[0-9]{2}/)
              ? moment(value, 'yyyy-MM')
              : null
          }
          modal={modal}
          onChange={handleChange}
          onSubmit={handleSubmit}
          preventDisabledDates={preventDisabledDates}
          disableFutureDates={disableFutureDates}
          disablePreviousDates={disablePreviousDates}
          locale={locale}
          minimumAge={minimumAge}
        />
      )}
    </Box>
  );
}

MonthPicker.propTypes = {
  assessment: PropTypes.bool,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  plain: PropTypes.bool,
  modal: PropTypes.bool,
  preventDisabledDates: PropTypes.bool,
  disabled: PropTypes.bool,
  disableFutureDates: PropTypes.bool,
  disablePreviousDates: PropTypes.bool,
  onChange: PropTypes.func,
};

export default MonthPicker;
